import { useState, useEffect } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Spinner } from './ui/Spinner';
import {
  getDomainSettings,
  getDomainInstructions,
  setCustomDomain,
  verifyDomain,
  removeDomain,
  getDomainPurchases,
  type DomainSettings,
  type DomainInstructions,
  type DomainPurchase,
} from '../api/domains';

interface DomainManagementProps {
  websiteId: string;
}

export function DomainManagement({ websiteId }: DomainManagementProps) {
  const [settings, setSettings] = useState<DomainSettings | null>(null);
  const [purchases, setPurchases] = useState<DomainPurchase[]>([]);
  const [instructions, setInstructions] = useState<DomainInstructions | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [customDomainInput, setCustomDomainInput] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    fetchDomainSettings();
  }, [websiteId]);

  async function fetchDomainSettings() {
    setLoading(true);
    setError(null);
    try {
      const [data, purchaseData] = await Promise.all([
        getDomainSettings(websiteId),
        getDomainPurchases(websiteId).catch(() => [] as DomainPurchase[]),
      ]);
      setSettings(data);
      setPurchases(purchaseData);
      if (data.customDomain) {
        setCustomDomainInput(data.customDomain);
      }
    } catch (err) {
      setError('Failed to load domain settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSetDomain() {
    if (!customDomainInput.trim()) return;

    setActionLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await setCustomDomain(websiteId, customDomainInput.trim());
      setSuccessMessage('Domain set successfully. Configure your DNS, then verify.');
      await fetchDomainSettings();
      // Auto-fetch instructions
      const instr = await getDomainInstructions(websiteId);
      setInstructions(instr);
      setShowInstructions(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set domain');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleVerifyDomain() {
    setActionLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await verifyDomain(websiteId);
      if (result.success) {
        setSuccessMessage(result.message);
      } else {
        setError(result.message || 'Verification failed');
      }
      await fetchDomainSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveDomain() {
    if (!confirm('Are you sure you want to remove the custom domain?')) return;

    setActionLoading(true);
    setError(null);

    try {
      await removeDomain(websiteId);
      setSuccessMessage('Domain removed');
      setCustomDomainInput('');
      setShowInstructions(false);
      setInstructions(null);
      await fetchDomainSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove domain');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleShowInstructions() {
    setActionLoading(true);
    try {
      const instr = await getDomainInstructions(websiteId);
      setInstructions(instr);
      setShowInstructions(true);
    } catch (err) {
      setError('Failed to load instructions');
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </Card>
    );
  }

  const getStatusBadge = (status: DomainSettings['verificationStatus']) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      verifying: 'bg-blue-100 text-blue-800',
      verified: 'bg-green-100 text-green-800',
      connected: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };

    const labels = {
      pending: 'Pending DNS',
      verifying: 'Verifying...',
      verified: 'Verified',
      connected: 'Connected',
      failed: 'Failed',
    };

    if (!status) return null;

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
          {successMessage}
        </div>
      )}

      {/* Current URL
          Leads with whatever the site actually answers on. Before the custom
          domain connects that is the pages.dev subdomain; after, it is the
          customer's own domain and the subdomain is scaffolding worth keeping
          visible but not worth leading with. */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Current URL</h3>
        {settings?.customDomain && settings.verificationStatus === 'connected' ? (
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <a
                href={`https://${settings.customDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline font-mono text-sm"
              >
                https://{settings.customDomain}
              </a>
              <span className="text-xs text-gray-500">(live)</span>
            </div>
            {settings.subdomain && (
              <div className="flex items-center gap-3">
                <a
                  href={settings.subdomain}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:underline font-mono text-xs"
                >
                  {settings.subdomain}
                </a>
                <span className="text-xs text-gray-400">(Pages subdomain, still works)</span>
              </div>
            )}
          </div>
        ) : settings?.subdomain ? (
          <div className="flex items-center gap-3">
            <a
              href={settings.subdomain}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline font-mono text-sm"
            >
              {settings.subdomain}
            </a>
            <span className="text-xs text-gray-500">(Cloudflare Pages subdomain)</span>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No subdomain assigned yet</p>
        )}
      </Card>

      {/* Custom Domain */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Custom Domain</h3>
          {settings?.verificationStatus && getStatusBadge(settings.verificationStatus)}
        </div>

        {settings?.verificationStatus === 'connected' ? (
          // Domain is connected
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">Domain Connected</span>
              </div>
              <a
                href={`https://${settings.customDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-700 hover:underline font-mono"
              >
                https://{settings.customDomain}
              </a>
              {settings.connectedAt && (
                <p className="text-sm text-green-600 mt-2">
                  Connected on {new Date(settings.connectedAt).toLocaleDateString()}
                </p>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemoveDomain}
              loading={actionLoading}
            >
              Remove Domain
            </Button>
          </div>
        ) : settings?.customDomain ? (
          // Domain set but not connected
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="font-medium text-yellow-800 mb-1">
                Waiting for DNS configuration
              </p>
              <p className="font-mono text-sm text-yellow-700">{settings.customDomain}</p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleVerifyDomain}
                loading={actionLoading}
              >
                Verify DNS
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleShowInstructions}
                loading={actionLoading}
              >
                View Instructions
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveDomain}
                loading={actionLoading}
              >
                Remove
              </Button>
            </div>
          </div>
        ) : (
          // No custom domain set
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Connect a custom domain to your site. You'll need to configure DNS at your domain registrar.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="example.com"
                value={customDomainInput}
                onChange={(e) => setCustomDomainInput(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={handleSetDomain}
                loading={actionLoading}
                disabled={!customDomainInput.trim()}
              >
                Set Domain
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Domain Purchases */}
      {purchases.length > 0 && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">Domain Purchases</h3>
          <div className="space-y-3">
            {purchases.map((p) => {
              const statusStyles: Record<string, string> = {
                active: 'bg-green-100 text-green-800',
                propagating: 'bg-yellow-100 text-yellow-800',
                pending: 'bg-gray-100 text-gray-800',
                failed: 'bg-red-100 text-red-800',
              };
              const statusLabels: Record<string, string> = {
                active: 'Active',
                propagating: 'Propagating NS',
                pending: 'Pending',
                failed: 'Failed',
              };
              const nsStatus = p.ns_propagation_status || 'pending';

              return (
                <div key={p.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono font-medium text-gray-900">{p.domain_name}</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[nsStatus] || 'bg-gray-100 text-gray-600'}`}>
                      {statusLabels[nsStatus] || nsStatus}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Registrar</span>
                      <p className="font-medium capitalize">{p.registrar}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Customer Price</span>
                      <p className="font-medium">${(p.purchase_price_cents / 100).toFixed(2)}/yr</p>
                    </div>
                    {p.cost_price_cents != null && (
                      <div>
                        <span className="text-gray-500">Our Cost</span>
                        <p className="font-medium">${(p.cost_price_cents / 100).toFixed(2)}</p>
                      </div>
                    )}
                    {p.markup_cents != null && (
                      <div>
                        <span className="text-gray-500">Margin</span>
                        <p className="font-medium text-green-700">${(p.markup_cents / 100).toFixed(2)}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Purchased</span>
                      <p className="font-medium">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    {p.expires_at && (
                      <div>
                        <span className="text-gray-500">Expires</span>
                        <p className="font-medium">{new Date(p.expires_at).toLocaleDateString()}</p>
                      </div>
                    )}
                    {p.provisioning_started_at && (
                      <div>
                        <span className="text-gray-500">Provisioning Started</span>
                        <p className="font-medium">{new Date(p.provisioning_started_at).toLocaleString()}</p>
                      </div>
                    )}
                    {p.provisioning_completed_at && (
                      <div>
                        <span className="text-gray-500">Provisioning Completed</span>
                        <p className="font-medium">{new Date(p.provisioning_completed_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* DNS Instructions */}
      {showInstructions && instructions && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">DNS Configuration</h3>
            <button
              onClick={() => setShowInstructions(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Steps */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Steps:</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                {instructions.steps.map((step, i) => (
                  <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
                ))}
              </ol>
            </div>

            {/* CNAME Record */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {instructions.cname.description}
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Type</span>
                  <p className="font-mono font-medium">{instructions.cname.type}</p>
                </div>
                <div>
                  <span className="text-gray-500">Name/Host</span>
                  <p className="font-mono font-medium">{instructions.cname.name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Target/Value</span>
                  <p className="font-mono font-medium text-primary-600">
                    {instructions.cname.target}
                  </p>
                </div>
              </div>
            </div>

            {/* Apex forwarding — a registrar setting rather than a DNS record,
                because registrars refuse a CNAME at the zone root. Without this
                the bare domain never reaches the site. */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {instructions.apex.description ?? 'Forward the root domain to www'}
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Type</span>
                  <p className="font-mono font-medium">Forwarding</p>
                </div>
                <div>
                  <span className="text-gray-500">From</span>
                  <p className="font-mono font-medium">{instructions.apex.from}</p>
                </div>
                <div>
                  <span className="text-gray-500">To</span>
                  <p className="font-mono font-medium text-primary-600 break-all">
                    {instructions.apex.to}
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              DNS changes can take 5-30 minutes to propagate. Click "Verify DNS" once you've added the records.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
